/*
    This file contains all the JD Ops Center Work Plan messaging handlers
    that may be used in batch sending of work plans.

    The integerId coming in with the messagingObject changes on a per-work plan basis (there will
    be one integerId per work plan sent).  The step integer is there for ordering the messages after they come
    in, with the general rule that each step corresponds with the following stages of the send work plans workflow:

    Step 1: initial "Processing Rec" message, creating/uploading shapefiles
    Step 2: creating or unarchiving JD clients
    Step 3: creating/unarchiving/retrieving fields, farms, boundaries, inputs
    Step 4: actual work plan sending
*/

import { IntlProvider, intlShape } from "react-intl";
import * as intlSelectors from "~/intl-provider/selectors";
import { messages } from "~/action-panel/components/rec-module/components/i18n-messages";
import { MSGTYPE, actions as notificationActions } from "~/notifications";
import { actions as eventsActions } from "~/recs-events";
import { put, select } from "redux-saga/effects";
import { pushToasterMessage } from "~/notifications/actions";

let intl: intlShape;
let locale: string;

interface MessageObject {
    integerId: string;
    permissionName?: string;
    clientName?: string;
    fieldName?: string;
    farmName?: string;
    customerName?: string;
    recName?: string;
    inputType?: string;
    numberOfInputs?: string;
    detail?: string;
    couldNotOperation?: string;
    numberFailed?: number;
    numberSucceeded?: number;
    totalNumber?: number;
}

function formatIntegerId(messageObject: MessageObject) {
    return messageObject.integerId ? "     Rec " + messageObject.integerId : "";
}

function getIntegerAsNumberFromIntegerId(messageObject: MessageObject) {
    return messageObject.integerId.length === 0
        ? null
        : parseInt(messageObject.integerId.replace(" :", ""));
}

function createMessageForWorkPlanFeedback(id: number, step: number, message: string) {
    return {
        id: id,
        step: step,
        message: message,
    };
}

const initializeIntl = function* () {
    locale = yield select(intlSelectors.getLocale);
    intl = new IntlProvider({ locale, messages }, {}).getChildContext().intl;
};

export const handleJdOpsCenterConnectionMissing = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.missingConnection, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleJdOpsCenterPermissionMissing = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.missingPermission, {
        permissionName: messageObject.permissionName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleFieldNotAssociatedWithJdOrganization = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.fieldNotAssociatedWithJdOrganization, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateControllerFile = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreatePrescriptionFile, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCreatedClientInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.createdClientInJdOpsCenter, {
        clientName: messageObject.clientName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 2, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateClientInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateClientInJdOpsCenter, {
        clientName: messageObject.clientName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotUnarchiveClientInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotUnarchiveClientInJdOpsCenter, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleFieldOverlapsWithExistingBoundary = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.fieldOverlapsWithExistingBoundary, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleFieldBoundaryChangeOccurred = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.fieldBoundaryChangeOccurred, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleRecAndFieldBoundaryMismatch = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.recAndFieldBoundaryMismatch, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleBoundaryWithSameNameAlreadyExists = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.boundaryWithSameNameAlreadyExists, {
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCreatedFieldInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.createdFieldInJdOpsCenter, {
        fieldName: messageObject.fieldName,
        farmName: messageObject.farmName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 3, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleUnArchivedFarmInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.unArchivedFarmInJdOpsCenter, {
        farmName: messageObject.farmName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 3, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleUnArchivedFieldInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.unArchivedFieldInJdOpsCenter, {
        fieldName: messageObject.fieldName,
        farmName: messageObject.farmName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 3, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleRetrievedFieldFromJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.retrievedFieldFromJdOpsCenter, {
        fieldName: messageObject.fieldName,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 3, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleSentWorkPlanToJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const prod = parseInt(messageObject.numberOfInputs) > 1 ? "Product Mixes" : "Product Mix";
    const message = intl.formatMessage(messages.sentWorkPlanToJdOpsCenter, {
        numberOfInputs: messageObject.numberOfInputs,
        prod,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 4, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
    yield put(eventsActions.fetchSelectedFieldRecs());
};

export const handleUpdatedWorkPlanInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const prod = parseInt(messageObject.numberOfInputs) > 1 ? "Product Mixes" : "Product Mix";
    const message = intl.formatMessage(messages.updatedWorkPlanInJdOpsCenter, {
        numberOfInputs: messageObject.numberOfInputs,
        prod,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 4, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
    yield put(eventsActions.fetchSelectedFieldRecs());
};

export const handleCouldNotCreateBoundaryInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateBoundaryInJdOpsCenter, {
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNot_InJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNot_InJdOpsCenter, {
        couldNotOperation: messageObject.couldNotOperation,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateFieldInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateFieldInJdOpsCenter, {
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateFileInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateFileInJdOpsCenter, {
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotUploadFileToJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotUploadFileToJdOpsCenter, {
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateInputInJdOpsCenter = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateInputInJdOpsCenter, {
        inputType: messageObject.inputType,
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleWorkPlanCreationOrUpdateForbidden = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.workPlanCreationOrUpdateForbidden, {
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 99, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateOrUpdateWorkPlan = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateOrUpdateWorkPlan, {
        detail: messageObject.detail,
        integerId: formatIntegerId(messageObject),
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 100, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateOrUpdateWorkPlanFinalBatch = function* (
    messageObject: MessageObject
) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateOrUpdateWorkPlanFinalBatch, {
        numberFailed: messageObject.numberFailed,
        totalNumber: messageObject.totalNumber,
    });
    yield put(pushToasterMessage(message, MSGTYPE.ERROR));
};

export const handleCreatedOrUpdatedWorkPlanFinalBatch = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.createdOrUpdatedWorkPlanFinalBatch, {
        numberSucceeded: messageObject.numberSucceeded,
        totalNumber: messageObject.totalNumber,
    });
    const messageAndInteger = createMessageForWorkPlanFeedback(1000, 100, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};

export const handleCouldNotCreateOrUpdateWorkPlanFinalSingle = function* () {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.couldNotCreateOrUpdateWorkPlanFinalSingle);
    yield put(pushToasterMessage(message, MSGTYPE.ERROR));
};

export const handleBatchMessageProcessingRec = function* (messageObject: MessageObject) {
    !intl ? yield initializeIntl() : null;
    const message = intl.formatMessage(messages.batchMessageProcessingRec, {
        integerId: messageObject.integerId,
        customerName: messageObject.customerName,
        farmName: messageObject.farmName ? `- ${messageObject.farmName}` : "",
        fieldName: messageObject.fieldName,
        recName: messageObject.recName,
    });
    const integerId = getIntegerAsNumberFromIntegerId(messageObject);
    const messageAndInteger = createMessageForWorkPlanFeedback(integerId, 1, message);
    yield put(notificationActions.sendToValidationFeedback(messageAndInteger));
};
